import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatLocalDateTimeInput } from '@/sections/insights/hunts/dateTimeLocal';
import { DEFAULT_AGENT_NAME } from '@/sections/insights/hunts/huntForm';

export const DEFAULT_MANDATE = `Autonomously assess the in-scope environment for exploitable paths that could lead to unauthorized access, privilege escalation, or sensitive data exposure. Focus on remote code execution, authentication weaknesses, default credentials, authentication and authorization bypasses, misconfigurations leading to compromise, exposed secrets, injection attacks, and privilege-escalation validation. The focus should be on exploitability of high to critical risks. Customers require demonstration and proof to engage remediation teams.`;

export const DEFAULT_GUARDRAILS = `Operate only within authorized scope. Do not perform denial-of-service, stress testing, brute force, password spraying, credential stuffing, phishing, social engineering, malware deployment, persistence, destructive actions, or bulk data exfiltration. Do not modify production data, change credentials, create accounts, alter configurations, restart services, disable security controls, clear logs, or evade monitoring. Use low-rate, low-concurrency testing. Stop or back off if testing causes errors, instability, lockout risk, throttling, or service degradation. If sensitive data is encountered, collect only minimal metadata or redacted evidence needed to prove exposure. When following redirects, stop before sending active probes, credentials, payloads, or exploit attempts to assets outside authorized scope. Passive observation of the redirect chain is permitted.`;

export const DEFAULT_FINISH_CRITERIA = `End the hunt early if any of the following occur:

1. A material critical compromise path has been validated.
2. Three consecutive iterations produce no new high-confidence findings.
3. If the agent encounters signs of service instability, account lockout risk, rate limiting, or operational impact, move on to another target.
4. If further progress would require brute force, phishing, persistence, or destructive testing, move on to another target.
5. Additional validation would materially increase risk without improving confidence.
6. The allotted duration expires.`;

interface LaunchHannibalDialogProps {
  open: boolean;
  mission: string;
  onLaunch: (config: {
    mandate: string;
    guardrails: string;
    finishCriteria: string;
    expiresAt: string;
    agentName: string;
  }) => void;
  onCancel: () => void;
  isPending: boolean;
  huntAgents?: Array<{ name: string; title?: string }>;
}

function LaunchHannibalForm({
  mission,
  onLaunch,
  onCancel,
  isPending,
  huntAgents,
}: Omit<LaunchHannibalDialogProps, 'open'>) {
  const [step, setStep] = useState<1 | 2>(1);
  const [mandate, setMandate] = useState(mission);
  const [guardrails, setGuardrails] = useState('');
  const [finishCriteria, setFinishCriteria] = useState('');
  const [durationHours, setDurationHours] = useState(24);
  const [agentName, setAgentName] = useState(DEFAULT_AGENT_NAME);

  const canLaunch = !isPending;

  const handleLaunch = () => {
    if (!canLaunch) return;
    const expiresAtDate = new Date();
    expiresAtDate.setHours(expiresAtDate.getHours() + durationHours);
    const expiresAt = formatLocalDateTimeInput(expiresAtDate);
    onLaunch({
      mandate: mandate.trim() || DEFAULT_MANDATE,
      guardrails: guardrails.trim() || DEFAULT_GUARDRAILS,
      finishCriteria: finishCriteria.trim() || DEFAULT_FINISH_CRITERIA,
      expiresAt,
      agentName,
    });
  };

  const showAgentSelector = huntAgents !== undefined && huntAgents.length > 1;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Launch Hannibal</DialogTitle>
        <DialogDescription>
          {step === 1
            ? 'Define the hunt objective and operational boundaries.'
            : 'Set exit criteria and hunt duration.'}
        </DialogDescription>
      </DialogHeader>

      <p className="text-chariot-text-secondary text-xs">Step {step} of 2</p>

      {step === 1 && (
        <div className="flex min-h-[520px] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-1.5">
              <Label htmlFor="mandate">Hunt Mandate</Label>
              {mission && (
                <span className="text-success ml-auto flex items-center gap-1 text-[11px] font-medium">
                  <span className="bg-success inline-block size-1.5 rounded-full" />
                  Pre-filled from conversation
                </span>
              )}
            </div>
            <Textarea
              id="mandate"
              rows={8}
              value={mandate}
              onChange={e => setMandate(e.target.value)}
              placeholder={DEFAULT_MANDATE}
            />
            {!mandate.trim() && (
              <button
                type="button"
                onClick={() => setMandate(DEFAULT_MANDATE)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default prompt
              </button>
            )}
            <p className="text-chariot-text-secondary text-xs">
              {!mandate.trim()
                ? 'The primary objective for the hunt agent. Leave empty to use the default prompt.'
                : mandate === DEFAULT_MANDATE
                  ? 'Default prompt targets exploitable high-to-critical risks.'
                  : 'Your custom mandate will focus the hunt.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="guardrails">Guardrails</Label>
            <Textarea
              id="guardrails"
              rows={10}
              value={guardrails}
              onChange={e => setGuardrails(e.target.value)}
              placeholder={DEFAULT_GUARDRAILS}
            />
            {!guardrails.trim() && (
              <button
                type="button"
                onClick={() => setGuardrails(DEFAULT_GUARDRAILS)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default guardrails
              </button>
            )}
            <p className="text-chariot-text-secondary text-xs">
              {!guardrails.trim()
                ? 'Boundaries evaluated per tool call by a separate LLM judge. Leave empty to use default guardrails.'
                : guardrails === DEFAULT_GUARDRAILS
                  ? 'Default guardrails enforce safe, authorized-scope-only testing.'
                  : 'Your custom guardrails will constrain the hunt.'}
            </p>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex min-h-[520px] flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="finishCriteria">Finish Criteria</Label>
            <Textarea
              id="finishCriteria"
              rows={10}
              value={finishCriteria}
              onChange={e => setFinishCriteria(e.target.value)}
              placeholder={DEFAULT_FINISH_CRITERIA}
            />
            {!finishCriteria.trim() && (
              <button
                type="button"
                onClick={() => setFinishCriteria(DEFAULT_FINISH_CRITERIA)}
                className="text-chariot-brand-primary text-left text-xs hover:underline"
              >
                Use default criteria
              </button>
            )}
            <p className="text-chariot-text-secondary text-xs">
              {!finishCriteria.trim()
                ? 'When met, the agent may end the hunt early. Leave empty to use default criteria.'
                : finishCriteria === DEFAULT_FINISH_CRITERIA
                  ? 'Default criteria end the hunt after validated compromise or stalled iterations.'
                  : 'Your custom criteria will control when the hunt stops.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Duration</Label>
            <div className="border-chariot-stroke flex overflow-hidden rounded-lg border">
              {[8, 24, 48, 72].map(hours => (
                <button
                  key={hours}
                  type="button"
                  onClick={() => setDurationHours(hours)}
                  className={cn(
                    'border-chariot-stroke flex flex-1 flex-col items-center justify-center gap-0.5 border-r py-3 transition-colors last:border-r-0',
                    durationHours === hours
                      ? 'bg-chariot-brand-primary text-white'
                      : 'bg-chariot-background-secondary text-chariot-text-secondary hover:bg-chariot-background-secondary/80'
                  )}
                >
                  <span className="text-base leading-none font-semibold">
                    {hours}
                  </span>
                  <span className="text-[10px] tracking-wider uppercase opacity-75">
                    hours
                  </span>
                </button>
              ))}
            </div>
          </div>

          {showAgentSelector && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="agentName">Agent</Label>
              <Select value={agentName} onValueChange={setAgentName}>
                <SelectTrigger id="agentName">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {huntAgents.map(agent => (
                    <SelectItem key={agent.name} value={agent.name}>
                      {agent.title ?? agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      <DialogFooter>
        {step === 1 ? (
          <>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={() => setStep(2)}>Next</Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={handleLaunch} disabled={!canLaunch}>
              {isPending ? 'Launching...' : 'Launch Hannibal'}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  );
}

export function LaunchHannibalDialog({
  open,
  mission,
  onLaunch,
  onCancel,
  isPending,
  huntAgents,
}: LaunchHannibalDialogProps) {
  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="max-w-2xl">
        <LaunchHannibalForm
          key={String(open)}
          mission={mission}
          onLaunch={onLaunch}
          onCancel={onCancel}
          isPending={isPending}
          huntAgents={huntAgents}
        />
      </DialogContent>
    </Dialog>
  );
}
